import { useMemo } from 'react';
import { Scissors } from 'lucide-react';

interface ColorStop {
  value: number;
  color: string;
}

// Optional status pill rendered under the gauge (e.g. solar curtailment).
// 'solid' = a confirmed state, 'outline' = a planned/suggested one.
export interface GaugeBadge {
  text: string;
  variant?: 'solid' | 'outline';
}

interface HorseshoeGaugeProps {
  value: number;
  min?: number;
  max: number;
  unit: string;
  label: string;
  colorStops: ColorStop[];
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  badge?: GaugeBadge;
}

export function HorseshoeGauge({
  value,
  min = 0,
  max,
  unit,
  label,
  colorStops,
  className = '',
  size = 'md',
  badge,
}: HorseshoeGaugeProps) {
  const percentage = useMemo(() => {
    const clamped = Math.max(min, Math.min(max, value));
    return ((clamped - min) / (max - min)) * 100;
  }, [value, min, max]);

  const currentColor = useMemo(() => {
    // Sort color stops by value
    const sorted = [...colorStops].sort((a, b) => a.value - b.value);

    // Find the appropriate color
    for (let i = sorted.length - 1; i >= 0; i--) {
      if (value >= sorted[i].value) {
        return sorted[i].color;
      }
    }
    return sorted[0]?.color || '#6B7280';
  }, [value, colorStops]);

  // SVG arc calculations
  const radius = 80;
  const strokeWidth = 12;
  const backgroundStrokeWidth = 4;
  const centerX = 100;
  const centerY = 90;

  // Arc spans from -210 to 30 degrees (240 degree arc)
  const startAngle = -210;
  const endAngle = 30;
  const totalAngle = endAngle - startAngle;

  const polarToCartesian = (cx: number, cy: number, r: number, angle: number) => {
    const rad = (angle * Math.PI) / 180;
    return {
      x: cx + r * Math.cos(rad),
      y: cy + r * Math.sin(rad),
    };
  };

  const describeArc = (cx: number, cy: number, r: number, start: number, end: number) => {
    const startPoint = polarToCartesian(cx, cy, r, start);
    const endPoint = polarToCartesian(cx, cy, r, end);
    const largeArcFlag = Math.abs(end - start) > 180 ? 1 : 0;
    return `M ${startPoint.x} ${startPoint.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${endPoint.x} ${endPoint.y}`;
  };

  const valueAngle = startAngle + (percentage / 100) * totalAngle;
  const backgroundPath = describeArc(centerX, centerY, radius, startAngle, endAngle);
  const valuePath = describeArc(centerX, centerY, radius, startAngle, valueAngle);

  const formatValue = (val: number) => {
    if (val >= 1000) {
      return `${(val / 1000).toFixed(1)}k`;
    }
    return val.toFixed(0);
  };

  // Size-based configurations
  const sizeConfig = {
    sm: { maxWidth: '140px', valueFontSize: '22px', unitFontSize: '11px', labelFontSize: '9px' },
    md: { maxWidth: '200px', valueFontSize: '28px', unitFontSize: '14px', labelFontSize: '10px' },
    lg: { maxWidth: '280px', valueFontSize: '36px', unitFontSize: '18px', labelFontSize: '12px' },
  };

  const config = sizeConfig[size];

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <svg viewBox="0 0 200 130" className="w-full" style={{ maxWidth: config.maxWidth }}>
        {/* Background arc */}
        <path
          d={backgroundPath}
          fill="none"
          stroke="currentColor"
          strokeWidth={backgroundStrokeWidth}
          className="text-gray-300 dark:text-gray-600"
          strokeLinecap="round"
        />

        {/* Value arc */}
        {percentage > 0 && (
          <path
            d={valuePath}
            fill="none"
            stroke={currentColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            style={{
              filter: `drop-shadow(0 0 6px ${currentColor}40)`,
            }}
          />
        )}

        {/* Value text */}
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          className="fill-gray-900 dark:fill-white font-semibold"
          style={{ fontSize: config.valueFontSize }}
        >
          {formatValue(value)}
          <tspan className="align-super" style={{ fontSize: config.unitFontSize }}>{unit}</tspan>
        </text>

        {/* Label */}
        <text
          x={centerX}
          y={centerY + 15}
          textAnchor="middle"
          className="fill-gray-500 dark:fill-gray-400 uppercase tracking-wider"
          style={{ fontSize: config.labelFontSize, letterSpacing: '0.05em' }}
        >
          {label}
        </text>
      </svg>
      {badge && (
        <span
          className={`-mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide ${
            badge.variant === 'outline'
              ? 'text-amber-600 dark:text-amber-400 border border-dashed border-amber-500/60'
              : 'text-amber-600 dark:text-amber-400 bg-amber-500/15 border border-amber-500/40'
          }`}
        >
          <Scissors className="w-3 h-3" />
          {badge.text}
        </span>
      )}
    </div>
  );
}

// Preset color stops for common use cases
export const solarColorStops: ColorStop[] = [
  { value: 0, color: '#FF6B35' },
  { value: 2000, color: '#F7C331' },
  { value: 4000, color: '#7BC950' },
  { value: 6000, color: '#2ECC71' },
  { value: 8000, color: '#27AE60' },
];

export const loadColorStops: ColorStop[] = [
  { value: 0, color: '#2ECC71' },
  { value: 1500, color: '#F7C331' },
  { value: 3000, color: '#E67E22' },
  { value: 4500, color: '#E74C3C' },
];
